import { useQuery } from '@tanstack/react-query';
import { MigrationBatchItemSchema } from '@taqon/contracts';
import { getPage } from '../../shared/api/client';
import { recordPath } from '../../shared/records/routes';
import { hasCapability, useSession } from '../../shared/session/session';
import { ErrorState, Section } from '../../shared/ui/page';
import { Facts, RecordLink } from '../finance/detail';
import { ITEMS } from './common';

/**
 * Where a record came from, on the record's own page (FRD-MIG-033: "identifiable as migrated …
 * sufficiently for a user to trace it back"). The trace is the accepted migration row that
 * produced the record — the record itself carries no migration column (ERD §8.8) — read through
 * `GET /api/v1/migration/items`. Shown only to a session that may view migration batches, and only
 * for a record that was loaded by migration; a record entered through the normal process shows
 * nothing.
 */
export function MigrationTrace({ recordType, recordId }: { recordType: string; recordId: string }) {
  const session = useSession();
  const canView = hasCapability(session.data, 'migration.batch:view');
  const trace = useQuery({
    queryKey: ['migration', 'trace', recordType, recordId],
    enabled: canView,
    queryFn: () =>
      getPage(
        `${ITEMS}?pageSize=5&filter[record_type]=${encodeURIComponent(recordType)}` +
          `&filter[record_id]=${encodeURIComponent(recordId)}`,
        MigrationBatchItemSchema,
      ),
  });
  if (!canView || trace.isPending) return null;
  if (trace.isError) {
    return (
      <Section title="Migration">
        <ErrorState message={trace.error.message} />
      </Section>
    );
  }
  const item = trace.data.items[0];
  if (item === undefined) return null;
  const batchPath = recordPath('migration.batch', item.batchId);
  return (
    <Section title="Migration">
      <Facts
        label="Migration source"
        facts={[
          [
            'Loaded by migration batch',
            batchPath === null ? (
              item.batchNumber
            ) : (
              <RecordLink to={batchPath}>{item.batchNumber}</RecordLink>
            ),
          ],
          ['Source row', String(item.sourceRowNumber)],
          ['Source key', item.sourceRowKey ?? '—'],
        ]}
      />
    </Section>
  );
}
